import { useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  alpha,
  Button,
  Link,
  ListItemText,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';
import { TABLE_HEAD_CELL_TYPE, TRANSACTION_PAYMENT_STATUS } from 'src/utils/common-types';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { BAR_CATEGORY_ID, KITCHEN_CATEGORY_ID } from 'src/config-global';
import { getItemTransactionsBySupplier, ISupplyListItem } from 'src/redux/slices/suppliers';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { ISupplierItem } from 'src/types/supplier';
import { ICommonItemTransactionListItem } from 'src/types/item';

import { paths } from '../../routes/paths';

const SUPPLIES_LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'itemCode', label: '#', align: 'left', primary: true },
  { id: 'itemName', label: 'Item name', align: 'left', primary: true },
  { id: 'locations', label: 'Locations', align: 'left', primary: true },
];

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'transactionNo', label: 'Transaction #', align: 'left', primary: true },
  {
    id: 'purchaseOrDispatchDate',
    label: 'Transaction Date',
    align: 'left',
    primary: true,
  },
  { id: 'paymentStatus', label: 'Payment Status', align: 'left', primary: true },
  { id: 'location', label: 'Location', align: 'left', primary: true },
  { id: 'updatedAt', label: 'Updated At', primary: true, align: 'left' },
];

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'items', label: 'Items' },
  { value: 'transactions', label: 'Transactions History' },
];

type Props = {
  currentSupplier?: ISupplierItem;
};

const defaultSortBy: { sortBy: string; order: 'asc' | 'desc' } = {
  sortBy: 'updatedAt',
  order: 'desc',
};

export default function SupplierDetails({ currentSupplier }: Props) {
  const dispatch = useAppDispatcher();

  const { user: authUser } = useAuthContext();

  const {
    supplies,
    transactions: { status, transactions, error },
  } = useAppSelector((state) => state.suppliers);
  const [tabValue, setTabValue] = useState('overview');

  const table = useTable();

  const { page, rowsPerPage } = table;

  const [sort, setSort] = useState(defaultSortBy);

  useEffect(() => {
    if (tabValue === 'transactions') {
      dispatch(
        getItemTransactionsBySupplier({
          supplier: currentSupplier?._id ?? '',
          page,
          limit: rowsPerPage,
          sortBy: sort.sortBy,
          sort: sort.order,
        })
      );
    }
  }, [tabValue, currentSupplier, dispatch, page, rowsPerPage, sort]);

  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_, value) => setTabValue(value)}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {TABS.map((tab) => (
              <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
            ))}
          </Tabs>

          {tabValue === 'overview' && (
            <Grid container spacing={2} mt={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle1">Basic Information</Typography>

                {hasPermission(
                  authUser,
                  'any',
                  AuthPermissions.UPDATE_SUPPLIER,
                  AuthPermissions.ADMIN_PERMISSION,
                  AuthPermissions.SUPER_ADMIN_PERMISSION
                ) && (
                  <Link
                    component={RouterLink}
                    href={paths.dashboard.suppliers.edit(currentSupplier?._id ?? '')}
                  >
                    <Button variant="text" startIcon={<Iconify icon="solar:pen-bold" />}>
                      Edit
                    </Button>
                  </Link>
                )}
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary={currentSupplier?.supplierName}
                  secondary="Company Name"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary={currentSupplier?.contactPerson}
                  secondary="Contact Person"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary={currentSupplier?.phoneNumber}
                  secondary="Phone Number"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary={currentSupplier?.email}
                  secondary="Email Address"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary={currentSupplier?.secondaryPhoneNumber}
                  secondary="Secondary Phone Number"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>
            </Grid>
          )}

          {tabValue === 'items' && (
            <Stack>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                {currentSupplier?.supplierName}
              </Typography>
              <Scrollbar>
                <Table stickyHeader size="small" sx={{ minWidth: 960, mt: 2 }}>
                  <TableHeadCustom headLabel={SUPPLIES_LINE_ITEM_HEADERS} />

                  <TableBody>
                    {supplies.status === 'loading' ? (
                      [...Array(10)].map((i, index) => <TableSkeleton key={index} />)
                    ) : (
                      <>
                        {supplies?.items.map((item: ISupplyListItem) => (
                          <TableRow hover key={item._id}>
                            <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                              {item.itemCode}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                              {item.itemName}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                              {item.category.map((category) => (
                                <Label
                                  color={
                                    category._id === KITCHEN_CATEGORY_ID ? 'secondary' : 'warning'
                                  }
                                  key={category._id}
                                  mr={2}
                                >
                                  <Link
                                    component={RouterLink}
                                    href={
                                      category._id === KITCHEN_CATEGORY_ID
                                        ? paths.dashboard.kitchen.root()
                                        : paths.dashboard.bar.root()
                                    }
                                  >
                                    {category.name}
                                  </Link>
                                </Label>
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}

                    {supplies.status !== 'failure' && supplies.items.length === 0 && (
                      <TableNoData
                        message="No items found"
                        notFound={supplies.items.length === 0}
                      />
                    )}

                    {supplies.status === 'failure' && (
                      <TableErrorData notFound error={supplies.error} />
                    )}
                  </TableBody>
                </Table>
              </Scrollbar>
            </Stack>
          )}

          {tabValue === 'transactions' && (
            <Stack>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                {currentSupplier?.supplierName}
              </Typography>
              <TableContainer
                sx={{ position: 'relative', overflow: 'hidden', height: '400px', mt: 2 }}
              >
                <Scrollbar>
                  <Table stickyHeader size="medium" sx={{ minWidth: 960 }}>
                    <TableHeadCustom
                      headLabel={LINE_ITEM_HEADERS}
                      order={sort.order}
                      orderBy={sort.sortBy}
                      rowCount={transactions.transactions.length}
                      onSort={(id) => {
                        if (id === sort.sortBy) {
                          setSort((prev) => ({
                            ...prev,
                            order: prev.order === 'asc' ? 'desc' : 'asc',
                          }));
                        } else {
                          setSort({
                            sortBy: id,
                            order: 'asc',
                          });
                        }
                      }}
                    />

                    <TableBody>
                      {status === 'loading' ? (
                        [...Array(10)].map((i, index) => <TableSkeleton key={index} />)
                      ) : (
                        <>
                          {transactions?.transactions.map(
                            (row: ICommonItemTransactionListItem, index: number) => (
                              <TableRow hover key={row._id}>
                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  <Link
                                    component={RouterLink}
                                    href={
                                      row.category._id === BAR_CATEGORY_ID
                                        ? paths.dashboard.bar.purchase.view(row._id)
                                        : paths.dashboard.kitchen.purchase.view(row._id)
                                    }
                                  >
                                    {row.transactionCode}
                                  </Link>
                                </TableCell>

                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  {fDateTime(row.purchaseOrDispatchDate)}
                                </TableCell>

                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  <Label
                                    variant="soft"
                                    color={
                                      (row.paymentStatus === TRANSACTION_PAYMENT_STATUS.PAID &&
                                        'success') ||
                                      (row.paymentStatus ===
                                        TRANSACTION_PAYMENT_STATUS.PARTIALLY_PAID &&
                                        'warning') ||
                                      (row.paymentStatus === TRANSACTION_PAYMENT_STATUS.NOT_PAID &&
                                        'error') ||
                                      'default'
                                    }
                                  >
                                    {row.paymentStatus.split('_').join(' ')}
                                  </Label>
                                </TableCell>

                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  <Label
                                    color={
                                      row.category._id === KITCHEN_CATEGORY_ID
                                        ? 'secondary'
                                        : 'warning'
                                    }
                                    mr={2}
                                  >
                                    <Link
                                      component={RouterLink}
                                      href={
                                        row.category._id === KITCHEN_CATEGORY_ID
                                          ? paths.dashboard.kitchen.root()
                                          : paths.dashboard.bar.root()
                                      }
                                    >
                                      {row.category.name}
                                    </Link>
                                  </Label>
                                </TableCell>

                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  {fDateTime(row.updatedAt)}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </>
                      )}

                      {status !== 'failure' && transactions.count === 0 && (
                        <TableNoData
                          message="No transactions found"
                          notFound={transactions.count === 0}
                        />
                      )}

                      {status === 'failure' && <TableErrorData notFound error={error} />}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
              <TablePaginationCustom
                count={transactions.count}
                page={table.page}
                rowsPerPage={table.rowsPerPage}
                onPageChange={table.onChangePage}
                onRowsPerPageChange={table.onChangeRowsPerPage}
                dense={table.dense}
                onChangeDense={table.onChangeDense}
              />
            </Stack>
          )}

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
            <Link component={RouterLink} href={paths.dashboard.suppliers.root}>
              <Button variant="outlined" color="inherit">
                Cancel
              </Button>
            </Link>
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
